import { api } from "@/api";
import { useState, useEffect } from "react";
import { RenderMarkdown } from "../Chat/ChatHelpers";
import { useNavigate } from "react-router";
import { Button } from "../ui/button";

interface WorkflowSandboxProps {
  id: string;
}

export function WorkflowSandbox({ id }: WorkflowSandboxProps) {
  const navigate = useNavigate();
  const [functionContents, setFunctionContents] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchContents = async () => {
      try {
        const { data, error } = await api.workflow({ id }).sandbox.get();
        if (error) {
          console.error(error);
          return;
        }
        setFunctionContents(data || null);
      } catch (error) {
        console.error(error);
      }
    };
    fetchContents();
  }, [id]);

  const handleRun = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await api.workflow({ id }).sandbox.post();
      if (error) {
        setResult(`Error: ${error}`);
        return;
      }
      if (data) {
        if (data.success) {
          setResult(data.stdout || null);
        } else {
          setResult(`${data.stdout}\n\nError: ${data.stderr}`);
        }
      }
    } catch (error) {
      setResult(`Error: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex flex-1">
        {/* Left Panel - Function Contents */}
        <div className="w-1/2 p-4 border-r">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Function Code</h2>
            <div className="flex gap-2">
              <Button
                variant={"outline"}
                onClick={() => navigate(`app/${id}/chat`)}
              >
                Back
              </Button>
              <Button onClick={handleRun} disabled={isLoading}>
                {isLoading ? "Running..." : "Run"}
              </Button>
            </div>
          </div>
          <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900 rounded p-4">
            {functionContents === null ? (
              <div>Loading...</div>
            ) : (
              <RenderMarkdown
                content={`\`\`\`typescript\n${functionContents}\`\`\``}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Results */}
        <div className="w-1/2 p-4">
          <h2 className="text-xl font-bold mb-4">Results</h2>
          <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900 rounded p-4">
            {result === null ? (
              <div className="text-gray-500">
                Run the function to see results
              </div>
            ) : (
              <pre className="whitespace-pre-wrap">{result}</pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
